import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./ZoomOverlay.css";

/**
 * AGRANDISSEMENT TEMPORAIRE D'UNE FICHE.
 *
 * POURQUOI. Deux fiches du module territoire portent une image d'explication :
 * la carte retournée d'un quizz, et la fiche d'une zone d'intérêt. Toutes deux
 * vivent dans un cadre volontairement petit — la carte de quizz doit tenir à
 * trois par ligne dans un bandeau, la fiche de zone d'intérêt doit laisser voir
 * le relief autour du marqueur qu'elle commente. Une image y arrive à 300 px de
 * large : lisible à un mètre, illisible depuis le fond d'une salle.
 *
 * Cet écran est la sortie de secours : le temps d'un commentaire, la fiche
 * passe au premier plan, l'image prend la place qu'elle mérite, et un clic ou
 * la touche Échap rend la carte. Rien n'est mémorisé — ce n'est pas un état du
 * cheminement, c'est un geste.
 *
 * IL EST PARTAGÉ, et c'est le point : les deux appelants vivent dans des
 * dossiers différents (territory_intro pour le quizz, terrain3d pour les zones
 * d'intérêt) et n'ont aucune raison d'agrandir différemment. Il est posé ici
 * parce que territory_intro dépend déjà de terrain3d, jamais l'inverse.
 */
export default function ZoomOverlay({ title, imageUrl, text, eyebrow = null, badge = null, onClose }) {
  // Échap ferme — et s'arrête là. L'écran d'étape écoute lui aussi Échap (pour
  // masquer le panneau) sur `window` : sans capture + arrêt de la propagation,
  // une seule frappe fermerait l'agrandissement ET replierait le panneau
  // derrière. La capture sur `document` passe avant la phase de bulle qui
  // atteindrait `window`.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose?.();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  const hasMedia = Boolean(imageUrl);
  const hasText = Boolean(text);

  // PORTAIL VERS LE <body>, ET CE N'EST PAS UN DÉTAIL. Les deux fiches qui
  // l'ouvrent vivent dans des conteneurs à `backdrop-filter` (le panneau
  // d'étape, les fiches de carte) : un `position: fixed` y est ancré au
  // conteneur et non à la fenêtre — l'agrandissement se retrouvait enfermé
  // dans le cadre de 300 px dont il devait justement sortir. Corollaire : les
  // jetons --t3d-* ne sont plus hérités, la feuille de style les redéclare.
  return createPortal(
    // Le fond ferme au clic : c'est le geste attendu d'une visionneuse, et il
    // évite d'avoir à viser la croix quand on présente debout.
    <div className="t3d-zoom" role="dialog" aria-modal="true" aria-label={title || "Agrandissement"}>
      <div className="t3d-zoom-backdrop" onClick={onClose} />

      <div className="t3d-zoom-sheet">
        <header className="t3d-zoom-head">
          {badge}
          <div className="t3d-zoom-headtext">
            {eyebrow && <span className="t3d-zoom-eyebrow">{eyebrow}</span>}
            {title && <h2 className="t3d-zoom-title">{title}</h2>}
          </div>
          <button type="button" className="t3d-zoom-close" onClick={onClose} aria-label="Fermer l'agrandissement">
            ×
          </button>
        </header>

        <div className={`t3d-zoom-body${hasMedia ? " has-media" : ""}${hasText ? " has-text" : ""}`}>
          {hasMedia && (
            <div className="t3d-zoom-media">
              <img src={imageUrl} alt="" />
            </div>
          )}
          {hasText && (
            <div className="t3d-zoom-text">
              <p>{text}</p>
            </div>
          )}
        </div>

        <p className="t3d-zoom-hint">Cliquez à côté ou appuyez sur Échap pour refermer.</p>
      </div>
    </div>,
    document.body
  );
}
